import React, { useState } from 'react'

import { TextLink } from '@octane/spark'
import { AxiosResponse } from 'axios'
import config from 'config'
import { useBrandConfig } from 'contexts/BrandConfigContextProvider'
import { setSessionKey } from 'storage/sessionKey'
import {
    checkForNewAppVersion,
    reloadForNewAppVersion,
    waitForAppVersionCheck,
} from 'utils/appVersion'

import { resetUnauthorizedState } from '../../api/auth'
import postLoginCredentials, {
    LoginParams,
} from '../../api/auth/login/postLoginCredentials'
import TermsOfUseModal from '../../components/TermsOfUseModal'
import AuthenticationFormBuilder from '../../containers/Authentication/AuthenticationFormBuilder'
import { FieldInstructionBundle } from '../../containers/FormBuilder'
import { password, username } from '../../form/fields'
import useAppDispatch from '../../hooks/useAppDispatch'
import { loadUser } from '../../state/user/actions'
import LoginFinePrint from './LoginFinePrint'
import { LoginPageContainer } from './LoginPage.styled'
import { getStatusMessage } from './util/getStatusMessage'

const loginFormInstructionBundle: FieldInstructionBundle = {
    username,
    password,
}

export default function LoginPage(): JSX.Element {
    const brandConfig = useBrandConfig()
    const dispatch = useAppDispatch()
    const [isTermsModalOpen, setTermsModalOpen] = useState(false)

    const [shouldShowStatusMessage, setShouldShowStatusMessage] =
        useState(false)

    const [statusMessage, setStatusMessage] = useState('')

    const onTermsOfUseClick = () => {
        setTermsModalOpen(!isTermsModalOpen)
    }

    const forgotPasswordLink = (
        <a href="/#/password-reset">
            <TextLink as="span">Forgot Password</TextLink>
        </a>
    )

    let icon

    if (brandConfig.loginLogoUrl && brandConfig.loginLogoWidth) {
        icon = (
            <img
                src={brandConfig.loginLogoUrl}
                width={brandConfig.loginLogoWidth}
                alt="Logotype"
            />
        )
    }

    const finePrint = <LoginFinePrint onTermsOfUseClick={onTermsOfUseClick} />

    const loadUserData = async (response: AxiosResponse) => {
        const message = getStatusMessage(response)

        if (response.status === 200) {
            try {
                await dispatch(loadUser())
            } catch {
                setStatusMessage(message)
                setShouldShowStatusMessage(true)
            }
        } else if (message) {
            setStatusMessage(message)
            setShouldShowStatusMessage(true)
        } else setShouldShowStatusMessage(false)
    }

    const setCatchForOtherErrors = () => {
        setStatusMessage('Error. Please contact us.')

        setShouldShowStatusMessage(true)
    }

    const isIFrame = () => {
        try {
            return window.self !== window.top
        } catch {
            return true
        }
    }

    return (
        <LoginPageContainer
            data-testid="loginPageContainer"
            backgroundImage={brandConfig.loginBackgroundImageUrl}
        >
            <AuthenticationFormBuilder
                instructions={loginFormInstructionBundle}
                onSubmitFn={(formikValues) => {
                    // In sandbox, always request the session key so the
                    // variant auth bridge (Authorization: Session <key>) has
                    // the key ready when the user selects a LOS variant.
                    // Outside sandbox this remains iframe-only (legacy iframe
                    // username/password login use case).
                    const returnSessionKey =
                        isIFrame() || config.ENVIRONMENT === 'sandbox'

                    setShouldShowStatusMessage(false)

                    // Check alongside authentication; only reload after success
                    // and after persisting any session key returned by the server.
                    const versionCheck = checkForNewAppVersion()

                    postLoginCredentials(
                        formikValues as LoginParams,
                        returnSessionKey,
                    )
                        .then(async (response: AxiosResponse) => {
                            if (response) {
                                // Reset unauthorized state on successful login
                                resetUnauthorizedState()

                                if (response.data?.session_key) {
                                    setSessionKey(response.data.session_key)
                                }

                                if (response.status === 200) {
                                    const targetVersion =
                                        await waitForAppVersionCheck(
                                            versionCheck,
                                        )

                                    if (reloadForNewAppVersion(targetVersion))
                                        return
                                }

                                await loadUserData(response)
                            }
                        })
                        .catch(() => setCatchForOtherErrors())
                }}
                submitButtonText="Sign In"
                headerTitle={brandConfig.loginTitle}
                headerSubtitle="Sign in with your existing account."
                textLink={forgotPasswordLink}
                isModal={false}
                finePrint={finePrint}
                statusMessage={statusMessage}
                shouldShowStatusMessage={shouldShowStatusMessage}
                icon={icon}
            />
            <TermsOfUseModal
                isOpen={isTermsModalOpen}
                handleClose={() => setTermsModalOpen(false)}
            />
        </LoginPageContainer>
    )
}
